// pages/dashboard/SubmissionDetail.jsx
// Steve's report review panel
// Shows full report, scorecard, edit loop and approve button

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

// Score colour tiers (Steve's language)
//   green  = strong        (21+)
//   yellow = comfort zone  (16+)
//   red    = discomfort    (below 16)
// Green (21+) was removed at Steve's request.
const SCORE_COMFORT = '#F1C40F';
const SCORE_DISCOMFORT = '#E74C3C';
// Darker variants for number text so it stays readable on white
const SCORE_COMFORT_TEXT = '#B7950B';
const SCORE_DISCOMFORT_TEXT = '#E74C3C';

const scoreBarColor = (v) => (v >= 16 ? SCORE_COMFORT : SCORE_DISCOMFORT);
const scoreTextColor = (v) => (v >= 16 ? SCORE_COMFORT_TEXT : SCORE_DISCOMFORT_TEXT);

const DIMENSION_NAMES = {
  goalOrientation: { name: 'Goal Orientation', left: 'Intentional', right: 'Adapting' },
  mission: { name: 'Mission', left: 'Shake Things Up', right: 'Maintain Stability' },
  relationships: { name: 'Relationships', left: 'Keep Formal Distance', right: 'Develop Casual Bonds' },
  reciprocity: { name: 'Reciprocity', left: 'Contribute Value', right: 'Learn & Ask for Help' },
  decisionMaking: { name: 'Decision Making', left: 'Impose', right: 'Facilitate' },
  pace: { name: 'Pace', left: 'Go Fast', right: 'Go Slow' },
  succession: { name: 'Succession', left: 'Replace/Clean Out', right: 'Develop Existing Talent' },
  loyalty: { name: 'Loyalty', left: 'Serve Hierarchy', right: 'Serve Team' },
};

const SubmissionDetail = () => {
  const { id } = useParams();
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPrompt, setEditPrompt] = useState('');
  const [editing, setEditing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('paid');
  const [previewMode, setPreviewMode] = useState(false); // toggle between edit view and preview
  const [accessSaving, setAccessSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null); // index of section being edited
  const [sectionDraft, setSectionDraft] = useState('');
  const [savingSection, setSavingSection] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [id]);

  // Generation runs in the background on the server, so without polling the
  // page sits on "Generating..." forever and Steve has to guess when to
  // refresh. Poll every 15s only while a generation is actually in flight.
  useEffect(() => {
    if (report?.paidReport?.status !== 'generating') return;
    const timer = setInterval(() => { fetchReport(); }, 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.paidReport?.status, id]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`/reports/${id}`);
      if (response.data.success) {
        setReport(response.data.data.report);
        setRevisions(response.data.data.revisions);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not load report' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editPrompt.trim()) {
      setMessage({ type: 'error', text: 'Please enter your edit instructions' });
      return;
    }
    setEditing(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.put(`/reports/${id}/edit`, { editPrompt });
      if (response.data.success) {
        setMessage({ type: 'success', text: `Revision ${response.data.data.revisionNumber} generated successfully` });
        setEditPrompt('');
        fetchReport();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Edit failed' });
    } finally {
      setEditing(false);
    }
  };

  // Split report content into sections on "## " headings so Steve can edit one block at a time
  const splitSections = (content) => {
    const lines = (content || '').split('\n');
    const sections = [];
    let current = { lines: [] };
    for (const line of lines) {
      if (/^##\s+/.test(line.trim()) && !/^###/.test(line.trim())) {
        if (current.lines.length) sections.push(current);
        current = { lines: [line] };
      } else {
        current.lines.push(line);
      }
    }
    if (current.lines.length) sections.push(current);
    return sections.map(sec => sec.lines.join('\n'));
  };

  const handleSaveSection = async (idx) => {
    setSavingSection(true);
    try {
      const sections = splitSections(report.paidReport.content);
      sections[idx] = sectionDraft;
      const newContent = sections.join('\n');
      const response = await axios.put(`/reports/${id}/content`, { content: newContent });
      if (response.data.success) {
        setMessage({ type: 'success', text: `Changes saved as revision ${response.data.data.revisionNumber}` });
        setEditingSection(null);
        setSectionDraft('');
        fetchReport();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save changes' });
    } finally {
      setSavingSection(false);
    }
  };

  const handlePreviewPDF = async () => {
    setLoadingPdf(true);
    try {
      const response = await axios.get(`/reports/${id}/preview-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not generate the PDF preview' });
    } finally {
      setLoadingPdf(false);
    }
  };

  // Render **bold** inline
  const renderBold = (text) => {
    const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((pt, j) =>
      pt.startsWith('**') && pt.endsWith('**') ? <strong key={j}>{pt.slice(2, -2)}</strong> : pt
    );
  };

  // Markdown-aware block renderer used by preview and section bodies
  const renderReportLines = (text, styleSet) => {
    const lines = (text || '').split('\n');
    const out = [];
    let i = 0;
    while (i < lines.length) {
      const trimmed = lines[i].trim();
      if (trimmed.startsWith('|')) {
        const tableLines = [];
        while (i < lines.length && lines[i].trim().startsWith('|')) { tableLines.push(lines[i].trim()); i++; }
        const rows = tableLines
          .filter(l => !/^\|[\s\-:|]+\|$/.test(l))
          .map(l => l.replace(/^\||\|$/g, '').split('|').map(c => c.trim()));
        out.push(
          <table key={`t${i}`} style={styleSet.mdTable}>
            <tbody>
              {rows.map((cells, r) => (
                <tr key={r}>
                  {cells.map((cell, c) => r === 0
                    ? <th key={c} style={styleSet.mdTh}>{cell.replace(/\*\*/g, '')}</th>
                    : <td key={c} style={styleSet.mdTd}>{renderBold(cell)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        );
        continue;
      }
      if (/^##\s+/.test(trimmed) && !/^###/.test(trimmed)) {
        out.push(<h2 key={i} style={styleSet.h2}>{trimmed.replace(/^#+\s*/, '')}</h2>);
      } else if (/^###\s+/.test(trimmed)) {
        out.push(<h3 key={i} style={styleSet.h3}>{trimmed.replace(/^#+\s*/, '')}</h3>);
      } else if (/^[-•]\s+/.test(trimmed)) {
        out.push(<p key={i} style={{ ...styleSet.p, paddingLeft: '16px' }}>• {renderBold(trimmed.replace(/^[-•]\s+/, ''))}</p>);
      } else if (trimmed === '') {
        out.push(<div key={i} style={{ height: '8px' }} />);
      } else {
        out.push(<p key={i} style={styleSet.p}>{renderBold(trimmed)}</p>);
      }
      i++;
    }
    return out;
  };

  const handleAccessChange = async (action) => {
    const confirmText = action === 'require_payment'
      ? `Require payment from ${user?.firstName}?\n\nTheir free access will be removed and they will see the payment page for the full report.`
      : `Grant ${user?.firstName} free access?\n\nThey will not be asked to pay for the full report.`;
    if (!window.confirm(confirmText)) return;
    setAccessSaving(true);
    try {
      const response = await axios.put(`/dashboard/users/${user._id}/access`, { action });
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message || 'Access updated' });
        fetchReport();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update access' });
    } finally {
      setAccessSaving(false);
    }
  };

const FAILURE_TEXT = 'Report generation encountered an issue';

  // Sends the approval. `acknowledgeStale` is only set when Steve has been shown
  // the "this is the previous version" warning and explicitly chose to proceed.
  const submitApproval = async (acknowledgeStale) => {
    setApproving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.put(`/reports/${id}/approve`, { acknowledgeStale });
      if (response.data.success) {
        setMessage({ type: 'success', text: `Report sent to ${response.data.data.sentTo}` });
        fetchReport();
      }
    } catch (err) {
      const code = err.response?.data?.code;
      const serverMsg = err.response?.data?.error || 'Approval failed';

      // The server refuses stale content unless it is acknowledged. Surface the
      // reason and offer the deliberate path rather than a dead end.
      if (code === 'REPORT_STALE_AFTER_FAILED_REGENERATION' && !acknowledgeStale) {
        setApproving(false);
        if (window.confirm(
          `${serverMsg}\n\nSend the PREVIOUS version of the report anyway?`
        )) {
          return submitApproval(true);
        }
        return;
      }

      setMessage({ type: 'error', text: serverMsg });
    } finally {
      setApproving(false);
    }
  };

  const handleApprove = async () => {
    // Block approval if report contains failure text
    if (report?.paidReport?.content?.includes(FAILURE_TEXT)) {
      alert('This report failed to generate. Please click Regenerate before approving.');
      return;
    }
    if (!window.confirm(`Send this report to ${report?.userId?.firstName} ${report?.userId?.lastName} at ${report?.userId?.email}?`)) return;
    await submitApproval(false);
  };

  const handleRegenerate = async () => {
    // Regenerating a report that has already gone out is deliberately allowed
    // (it is how a sent report gets refreshed against an updated prompt), but
    // the recipient keeps the copy they were already sent, so the stored
    // version will no longer match theirs. Say so before proceeding.
    const prompt = isSent
      ? 'This report has already been sent. Regenerating replaces the stored version with a new one.\n\nThe recipient keeps the copy they were already emailed. Nothing is re-sent automatically.\n\nContinue?'
      : 'Regenerate this report using Claude AI? This will create a new revision.';
    if (!window.confirm(prompt)) return;

    setEditing(true);
    try {
      // The server sets status to 'generating', replies immediately, and runs
      // the Claude call in the background. It does NOT mean the report is
      // ready. Saying "regenerated" here was wrong by about 5 minutes and told
      // Steve to click a button that is not on screen during generation.
      await axios.post(`/reports/${id}/regenerate`);
      setMessage({
        type: 'success',
        text: 'Regeneration started. This usually takes 5 to 7 minutes. This page updates on its own, no need to refresh.',
      });
      fetchReport();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Regeneration failed' });
    } finally {
      setEditing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋', active: true },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  if (loading) {
    return (
      <div style={s.loadingPage}>
        <div style={s.spinner} />
        <p>Loading report...</p>
      </div>
    );
  }

  const user = report?.userId;
  const submission = report?.submissionId;
  const dimensionScores = submission?.dimensionScores ? Object.entries(submission.dimensionScores) : [];
  const isPendingReview = report?.paidReport?.status === 'pending_review';
  const isSent = report?.paidReport?.status === 'sent';

  // Two different problems, two different warnings.
  // hasFailedContent: generation failed and the placeholder text is stored.
  // hasStaleContent:  a REGENERATION failed, so the content below is the
  //   previous version and not the regenerated one. This looks completely
  //   normal on screen, which is exactly why it needs saying out loud.
  const isGenerating = report?.paidReport?.status === 'generating';
  const hasFailedContent = !!report?.paidReport?.content?.includes(FAILURE_TEXT);
  const hasStaleContent = !hasFailedContent && !!report?.generationError && !isSent;

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        {/* Back button */}
        <button style={s.backBtn} onClick={() => navigate('/admin/submissions')}>
          ← Back to Submissions
        </button>

        {/* User Info Header */}
        <div style={s.userHeader}>
          <div style={s.userAvatar}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          <div style={s.userInfo}>
            <h1 style={s.userName}>{user?.firstName} {user?.lastName}</h1>
            <p style={s.userDetails}>{user?.jobTitle || 'Executive'} {user?.companyName ? `at ${user.companyName}` : ''}</p>
            <p style={s.userEmail}>{user?.email}</p>
          </div>
          <div style={s.headerActions}>
            {isGenerating && (
              <div style={s.generatingBar}>
                <span style={s.generatingDot} />
                Generating report, usually 5 to 7 minutes. This updates automatically.
              </div>
            )}
            {isPendingReview && (
              <>
                <button style={s.btnRegenerate} onClick={handleRegenerate} disabled={editing}>
                  {editing ? 'Processing...' : 'Regenerate'}
                </button>
                <button style={s.btnPreviewPdf} onClick={handlePreviewPDF} disabled={loadingPdf}>
                  {loadingPdf ? 'Building PDF...' : 'Preview PDF'}
                </button>
                <button style={s.btnApprove} onClick={handleApprove} disabled={approving || editing || hasFailedContent || hasStaleContent}>
                  {approving ? 'Sending...' : '✅ Approve and Send'}
                </button>
              </>
            )}
            {isSent && (
              <>
                {/* Regeneration is allowed after sending so a report can be
                    refreshed against an updated prompt. It replaces the stored
                    version; the client keeps whatever they were already sent,
                    so handleRegenerate warns before proceeding. */}
                <button style={s.btnRegenerate} onClick={handleRegenerate} disabled={editing}>
                  {editing ? 'Processing...' : 'Regenerate'}
                </button>
                <button style={s.btnPreviewPdf} onClick={handlePreviewPDF} disabled={loadingPdf}>
                  {loadingPdf ? 'Building PDF...' : '⬇ Download PDF'}
                </button>
                <div style={s.sentBadge}>✅ Report Sent</div>
              </>
            )}
          </div>
        </div>

        {/* Access and payment control for Steve */}
        {user && (
          <div style={s.accessPanel}>
            <div style={s.accessInfo}>
              <span style={s.accessLabel}>Access:</span>
              <span style={s.accessValue}>
                {{
                  individual: '👤 Individual (pays for full report)',
                  shared_link: '🔗 Shared Link (free access)',
                  enterprise_passcode: '🏢 Enterprise Passcode (company pays)',
                  enterprise_link: '🏢 Enterprise Member Link (company pays)',
                }[user.accessType] || user.accessType || 'Unknown'}
              </span>
            </div>
            <div style={s.accessActions}>
              {user.accessType === 'individual' ? (
                <button
                  style={s.btnAccessGrant}
                  onClick={() => handleAccessChange('grant_free')}
                  disabled={accessSaving}
                >
                  {accessSaving ? 'Saving...' : 'Grant Free Access'}
                </button>
              ) : (
                <button
                  style={s.btnAccessRequire}
                  onClick={() => handleAccessChange('require_payment')}
                  disabled={accessSaving}
                >
                  {accessSaving ? 'Saving...' : 'Require Payment'}
                </button>
              )}
            </div>
          </div>
        )}

        {message.text && (
          <div style={{ ...s.messageBox, ...(message.type === 'success' ? s.messageSuccess : s.messageError) }}>
            {message.text}
          </div>
        )}

        <div style={s.twoCol}>

          {/* Left: Scorecard */}
          <div style={s.leftCol}>
            <div style={s.card}>
              <h2 style={s.cardTitle}>Comfort Zone Scorecard</h2>
              <div style={s.scoreLegend}>
                <span style={s.legendItem}><span style={{ ...s.legendSwatch, backgroundColor: SCORE_COMFORT }} /> Comfort zone (16+)</span>
                <span style={s.legendItem}><span style={{ ...s.legendSwatch, backgroundColor: SCORE_DISCOMFORT }} /> Potential derailer (below 16)</span>
              </div>
              {dimensionScores.length > 0 ? (
                <div style={s.scorecard}>
                  {dimensionScores.map(([dimId, scores]) => {
                    const dimInfo = DIMENSION_NAMES[dimId];
                    if (!dimInfo) return null;
                    return (
                      <div key={dimId} style={s.scorecardRow}>
                        <div style={s.dimName}>{dimInfo.name}</div>
                        <div style={s.scoreRow}>
                          <span style={s.poleName}>{dimInfo.left}</span>
                          <span style={{ ...s.score, color: scoreTextColor(scores.leftScore) }}>
                            {scores.leftScore}
                          </span>
                          <div style={s.scoreBar}>
                            <div style={{ ...s.scoreBarLeft, width: `${(scores.leftScore / 24) * 100}%`, backgroundColor: scoreBarColor(scores.leftScore) }} />
                            <div style={{ ...s.scoreBarRight, width: `${(scores.rightScore / 24) * 100}%`, backgroundColor: scoreBarColor(scores.rightScore) }} />
                          </div>
                          <span style={{ ...s.score, color: scoreTextColor(scores.rightScore) }}>
                            {scores.rightScore}
                          </span>
                          <span style={s.poleName}>{dimInfo.right}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={s.noData}>Scorecard data not available</p>
              )}
            </div>

            {/* Revision History */}
            {revisions.length > 0 && (
              <div style={s.card}>
                <h2 style={s.cardTitle}>Revision History</h2>
                {revisions.map((rev) => (
                  <div key={rev._id} style={s.revisionItem}>
                    <div style={s.revisionHeader}>
                      <span style={s.revisionNum}>Revision {rev.revisionNumber}</span>
                      <span style={s.revisionDate}>{new Date(rev.createdAt).toLocaleString()}</span>
                    </div>
                    {rev.editPrompt && (
                      <p style={s.revisionPrompt}>Edit prompt: "{rev.editPrompt}"</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Report Content */}
          <div style={s.rightCol}>

            {/* Tabs */}
            <div style={s.tabs}>
              <button style={{ ...s.tab, ...(activeTab === 'paid' ? s.tabActive : {}) }} onClick={() => setActiveTab('paid')}>
                Paid Report
              </button>
              <button style={{ ...s.tab, ...(activeTab === 'free' ? s.tabActive : {}) }} onClick={() => setActiveTab('free')}>
                Free Report
              </button>
            </div>

            <div style={s.card}>
              {activeTab === 'paid' ? (
                <>
                  <div style={s.reportStatusRow}>
                    <h2 style={s.cardTitle}>Paid Detailed Report</h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {report?.paidReport?.content && !hasFailedContent && (
                        <button
                          style={{ ...s.btnPreview, ...(previewMode ? s.btnPreviewActive : {}) }}
                          onClick={() => setPreviewMode(!previewMode)}
                        >
                          {previewMode ? '✏️ Edit View' : '👁 Preview'}
                        </button>
                      )}
                      {report?.paidReport?.status === 'pending_review' && (
                        <span style={{ backgroundColor: '#FEF9E7', color: '#A04000', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: '500' }}>Pending Your Review</span>
                      )}
                      {report?.paidReport?.status === 'sent' && (
                        <span style={{ backgroundColor: '#E8F8EF', color: '#1E8449', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: '500' }}>Sent</span>
                      )}
                      {report?.paidReport?.status === 'generating' && (
                        <span style={{ backgroundColor: '#EBF5FB', color: '#1A5276', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: '500' }}>Generating...</span>
                      )}
                    </div>
                  </div>

                  {/* Generation failed outright: the stored content is placeholder text. */}
                  {hasFailedContent && (
                    <div style={s.bannerDanger}>
                      ⚠️ Report generation failed. Please click Regenerate before approving. Do not send this report to the client.
                    </div>
                  )}

                  {/* Regeneration failed: what is shown below is the PREVIOUS version.
                      Nothing about it looks wrong, so this has to be stated explicitly. */}
                  {hasStaleContent && (
                    <div style={s.bannerWarning}>
                      ⚠️ The last regeneration failed, so the report below is the <strong>previous version</strong>, not the regenerated one.
                      Click Regenerate to try again. If you send it as is, you will be sending the older report.
                      {report?.generationError && (
                        <div style={s.bannerDetail}>Error: {report.generationError}</div>
                      )}
                    </div>
                  )}
                  {/* PREVIEW MODE */}
                  {previewMode && report?.paidReport?.content && (
                    <div style={s.previewContainer}>
                      <div style={s.previewBanner}>
                        👁 Preview mode. This is exactly how the report will look to {report?.userId?.firstName}. Close preview to make edits.
                      </div>
                      <div style={s.previewContent}>
                        <div style={s.previewHeader}>
                          <div style={s.previewLogoWrap}>
                            <span style={s.previewLogoExec}>EXECUTIVE</span>
                            <span style={s.previewLogoSpring}>Springboard</span>
                          </div>
                          <h1 style={s.previewTitle}>Leadership Assessment Report</h1>
                          <p style={s.previewSubtitle}>{report?.userId?.firstName} {report?.userId?.lastName}</p>
                          <p style={s.previewMeta}>{report?.userId?.jobTitle}{report?.userId?.companyName ? ` · ${report?.userId?.companyName}` : ''}</p>
                          <p style={s.previewDate}>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                          <p style={s.previewConfidential}>CONFIDENTIAL</p>
                        </div>
                        <div style={s.previewBody}>
                          {renderReportLines(report.paidReport.content, { h2: s.previewSection, h3: s.previewSubsection, p: s.previewPara, mdTable: s.mdTable, mdTh: s.mdTh, mdTd: s.mdTd })}
                        </div>
                        <div style={s.previewFooter}>
                          Executive Springboard · execspringboard.com · Confidential
                        </div>
                      </div>
                    </div>
                  )}

                  {/* EDIT VIEW */}
                  {!previewMode && report?.paidReport?.content && (
                    <div style={s.reportContent}>
                      {splitSections(report.paidReport.content).map((sectionText, idx) => (
                        <div key={idx} style={{ ...s.sectionBlock, ...(editingSection === idx ? s.sectionBlockEditing : {}) }}>
                          {editingSection === idx ? (
                            <>
                              <textarea
                                style={s.sectionTextarea}
                                value={sectionDraft}
                                onChange={e => setSectionDraft(e.target.value)}
                                rows={Math.min(22, Math.max(8, sectionDraft.split('\n').length + 2))}
                                disabled={savingSection}
                              />
                              <div style={s.sectionEditActions}>
                                <button style={s.btnSectionSave} onClick={() => handleSaveSection(idx)} disabled={savingSection}>
                                  {savingSection ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button style={s.btnSectionCancel} onClick={() => { setEditingSection(null); setSectionDraft(''); }} disabled={savingSection}>
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              {isPendingReview && (
                                <button
                                  style={s.btnSectionEdit}
                                  onClick={() => { setEditingSection(idx); setSectionDraft(sectionText); }}
                                >
                                  Edit This Section
                                </button>
                              )}
                              {renderReportLines(sectionText, { h2: s.reportSection, h3: s.reportSubsection, p: s.reportPara, mdTable: s.mdTable, mdTh: s.mdTh, mdTd: s.mdTd })}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No content state */}
                  {!previewMode && !report?.paidReport?.content && (
                    <div style={s.noData}>
                      <p>Report is being generated. Please check back in a few minutes.</p>
                      <button style={s.btnRefresh} onClick={fetchReport}>Refresh</button>
                    </div>
                  )}

                  {/* Edit Box */}
                  {(isPendingReview) && (
                    <div style={s.editBox}>
                      <h3 style={s.editTitle}>Request Edit</h3>
                      <p style={s.editDesc}>Describe what you want changed. Claude will revise the report maintaining the same structure.</p>
                      <textarea
                        style={s.editTextarea}
                        placeholder="e.g. The tone in the Succession section feels too harsh. Please soften it while keeping the core message. Also add a specific example in the 90 day roadmap for the Decision Making dimension."
                        value={editPrompt}
                        onChange={e => setEditPrompt(e.target.value)}
                        rows={4}
                        disabled={editing}
                      />
                      <div style={s.editActions}>
                        <button style={s.btnEdit} onClick={handleEdit} disabled={editing || !editPrompt.trim()}>
                          {editing ? 'Generating revision...' : 'Request Edit'}
                        </button>
                        <button style={s.btnApproveInline} onClick={handleApprove} disabled={approving || editing || report?.paidReport?.content?.includes(FAILURE_TEXT)}>
                          {approving ? 'Sending...' : '✅ Approve and Send Report'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h2 style={s.cardTitle}>Free Topline Report</h2>
                  {report?.freeReport?.content ? (
                    <div style={s.reportContent}>
                      {report.freeReport.content.split('\n').map((line, i) => {
                        if (line.match(/^[1-9]\.|^[A-Z][A-Z\s]{3,}:?$/)) {
                          return <h3 key={i} style={s.reportSection}>{line}</h3>;
                        }
                        if (line.trim() === '') return <br key={i} />;
                        return <p key={i} style={s.reportPara}>{line}</p>;
                      })}
                    </div>
                  ) : (
                    <p style={s.noData}>Free report not available</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  loadingPage: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' },
  spinner: { width: '40px', height: '40px', border: '4px solid var(--color-light-gray)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '24px 32px', overflowY: 'auto' },
  backBtn: { background: 'none', border: 'none', color: 'var(--color-teal)', fontSize: '14px', cursor: 'pointer', marginBottom: '20px', fontWeight: '500', padding: 0 },
  userHeader: { display: 'flex', alignItems: 'center', gap: '20px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '20px 24px', marginBottom: '20px', flexWrap: 'wrap' },
  userAvatar: { width: '56px', height: '56px', borderRadius: '50%', backgroundColor: 'var(--color-teal)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700', flexShrink: 0 },
  userInfo: { flex: 1 },
  userName: { fontSize: '20px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  userDetails: { fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '2px' },
  userEmail: { fontSize: '13px', color: 'var(--color-teal)' },
  headerActions: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  btnApprove: { backgroundColor: '#27AE60', color: '#fff', padding: '18px 36px', borderRadius: '12px', border: 'none', fontSize: '18px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 3px 10px rgba(39, 174, 96, 0.4)' },
  accessPanel: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '14px 20px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' },
  accessInfo: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' },
  accessLabel: { fontWeight: '600', color: 'var(--color-primary)' },
  accessValue: { color: 'var(--color-text)' },
  accessActions: { display: 'flex', gap: '8px' },
  btnAccessGrant: { backgroundColor: '#E8F8EF', color: '#1E8449', padding: '8px 16px', borderRadius: '8px', border: '1px solid #1E8449', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnAccessRequire: { backgroundColor: '#FDEDEC', color: '#943126', padding: '8px 16px', borderRadius: '8px', border: '1px solid #943126', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnPreviewPdf: { backgroundColor: '#fff', color: 'var(--color-primary)', padding: '16px 24px', borderRadius: '12px', border: '2px solid var(--color-primary)', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  scoreLegend: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '14px', marginTop: '-6px' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: 'var(--color-text-light)' },
  legendSwatch: { width: '11px', height: '11px', borderRadius: '3px', display: 'inline-block' },
  sectionBlock: { position: 'relative', padding: '12px 14px', borderRadius: '10px', border: '1px solid transparent', marginBottom: '6px' },
  sectionBlockEditing: { border: '1px solid var(--color-teal)', backgroundColor: '#F8FBFC' },
  btnSectionEdit: { position: 'absolute', top: '10px', right: '10px', backgroundColor: '#EBF5FB', color: '#1A5276', padding: '5px 12px', borderRadius: '6px', border: '1px solid #AED6F1', fontSize: '11.5px', fontWeight: '500', cursor: 'pointer' },
  sectionTextarea: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.6', resize: 'vertical', boxSizing: 'border-box' },
  sectionEditActions: { display: 'flex', gap: '8px', marginTop: '10px' },
  btnSectionSave: { backgroundColor: '#27AE60', color: '#fff', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  btnSectionCancel: { backgroundColor: '#fff', color: 'var(--color-text)', padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '13px', cursor: 'pointer' },
  reportSubsection: { fontSize: '13.5px', fontWeight: '600', color: 'var(--color-primary)', margin: '12px 0 4px' },
  mdTable: { width: '100%', borderCollapse: 'collapse', margin: '10px 0', fontSize: '12px' },
  mdTh: { backgroundColor: 'var(--color-primary)', color: '#fff', padding: '7px 9px', textAlign: 'left', border: '1px solid var(--color-border)', fontWeight: '600' },
  mdTd: { padding: '7px 9px', border: '1px solid var(--color-border)', color: 'var(--color-text)', verticalAlign: 'top' },
  btnRegenerate: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '10px 16px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  generatingBar: { display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#EBF5FB', color: '#1A5276', border: '1px solid #AED6F1', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '500' },
  generatingDot: { width: '14px', height: '14px', borderRadius: '50%', border: '2px solid rgba(26,82,118,0.25)', borderTopColor: '#1A5276', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
  bannerDanger: { backgroundColor: '#FDEDEC', border: '1px solid #E74C3C', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#C0392B', fontWeight: '500', lineHeight: '1.6' },
  bannerWarning: { backgroundColor: '#FEF9E7', border: '1px solid #E67E22', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#A04000', fontWeight: '500', lineHeight: '1.6' },
  bannerDetail: { marginTop: '8px', fontSize: '12px', fontWeight: '400', color: '#7E5109', wordBreak: 'break-word' },
  sentBadge: { backgroundColor: '#E8F8EF', color: '#1E8449', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '500' },
  messageBox: { padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  messageSuccess: { backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)', border: '1px solid #A9DFBF' },
  messageError: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB' },
  twoCol: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: '20px', alignItems: 'start' },
  leftCol: { display: 'flex', flexDirection: 'column', gap: '16px' },
  rightCol: { display: 'flex', flexDirection: 'column', gap: '0' },
  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', padding: '20px', marginBottom: '16px' },
  cardTitle: { fontSize: '16px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '16px' },
  scorecard: { display: 'flex', flexDirection: 'column', gap: '10px' },
  scorecardRow: { paddingBottom: '10px', borderBottom: '1px solid var(--color-border)' },
  dimName: { fontSize: '12px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px' },
  scoreRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  poleName: { fontSize: '10px', color: 'var(--color-text-light)', width: '60px', textAlign: 'right', flexShrink: 0 },
  score: { fontSize: '14px', fontWeight: '700', width: '24px', textAlign: 'center', flexShrink: 0 },
  scoreBar: { flex: 1, height: '6px', display: 'flex', gap: '2px', borderRadius: '3px', overflow: 'hidden', backgroundColor: 'var(--color-light-gray)' },
  scoreBarLeft: { height: '100%', borderRadius: '3px' },
  scoreBarRight: { height: '100%', borderRadius: '3px', marginLeft: 'auto' },
  noData: { color: 'var(--color-text-light)', fontSize: '14px', textAlign: 'center', padding: '20px 0' },
  btnRefresh: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '8px 16px', borderRadius: '6px', border: 'none', fontSize: '13px', cursor: 'pointer', marginTop: '12px' },
  btnPreview: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '6px 14px', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '12px', cursor: 'pointer', fontWeight: '500' },
  btnPreviewActive: { backgroundColor: 'var(--color-primary)', color: '#fff', border: '1px solid var(--color-primary)' },
  previewContainer: { marginTop: '8px' },
  previewBanner: { backgroundColor: '#FEF9E7', color: '#A04000', border: '1px solid #F5C6A0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' },
  previewContent: { border: '2px solid var(--color-border)', borderRadius: '10px', overflow: 'hidden', maxHeight: '700px', overflowY: 'auto' },
  previewHeader: { backgroundColor: 'var(--color-primary)', padding: '32px', textAlign: 'center' },
  previewLogoWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', marginBottom: '20px' },
  previewLogoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  previewLogoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  previewTitle: { fontSize: '22px', fontWeight: '700', color: '#fff', marginBottom: '8px' },
  previewSubtitle: { fontSize: '16px', fontWeight: '600', color: 'rgba(255,255,255,0.9)', marginBottom: '4px' },
  previewMeta: { fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '2px' },
  previewDate: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' },
  previewConfidential: { fontSize: '10px', letterSpacing: '3px', color: 'rgba(255,255,255,0.3)', fontWeight: '700' },
  previewBody: { padding: '24px', backgroundColor: '#fff' },
  previewSection: { fontSize: '14px', fontWeight: '700', color: 'var(--color-primary)', marginTop: '20px', marginBottom: '8px', paddingBottom: '6px', borderBottom: '2px solid var(--color-teal)' },
  previewSubsection: { fontSize: '13px', fontWeight: '600', color: 'var(--color-primary)', marginTop: '12px', marginBottom: '4px' },
  previewPara: { fontSize: '12px', color: 'var(--color-text)', lineHeight: '1.7', marginBottom: '4px' },
  previewFooter: { backgroundColor: 'var(--color-off-white)', borderTop: '1px solid var(--color-border)', padding: '12px 24px', textAlign: 'center', fontSize: '11px', color: 'var(--color-text-light)' },
  revisionItem: { padding: '10px', backgroundColor: 'var(--color-off-white)', borderRadius: '8px', marginBottom: '8px' },
  revisionHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  revisionNum: { fontSize: '13px', fontWeight: '600', color: 'var(--color-primary)' },
  revisionDate: { fontSize: '11px', color: 'var(--color-text-light)' },
  revisionPrompt: { fontSize: '12px', color: 'var(--color-text-light)', fontStyle: 'italic' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '0', backgroundColor: '#fff', borderRadius: '12px 12px 0 0', border: '1px solid var(--color-border)', borderBottom: 'none', padding: '4px 4px 0' },
  tab: { padding: '10px 20px', border: 'none', backgroundColor: 'transparent', color: 'var(--color-text-light)', fontSize: '14px', fontWeight: '500', cursor: 'pointer', borderRadius: '8px 8px 0 0' },
  tabActive: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-primary)', fontWeight: '600' },
  reportStatusRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  reportContent: { maxHeight: '500px', overflowY: 'auto', paddingRight: '8px' },
  reportSection: { fontSize: '14px', fontWeight: '700', color: 'var(--color-primary)', marginTop: '16px', marginBottom: '6px', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px' },
  reportPara: { fontSize: '13px', color: 'var(--color-text)', lineHeight: '1.7', marginBottom: '4px' },
  editBox: { backgroundColor: 'var(--color-off-white)', borderRadius: '10px', padding: '20px', marginTop: '20px', border: '1px solid var(--color-border)' },
  editTitle: { fontSize: '15px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '6px' },
  editDesc: { fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '12px', lineHeight: '1.5' },
  editTextarea: { width: '100%', padding: '12px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: '1.6' },
  editActions: { display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' },
  btnEdit: { backgroundColor: 'var(--color-primary)', color: '#fff', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnApproveInline: { backgroundColor: '#27AE60', color: '#fff', padding: '14px 28px', borderRadius: '10px', border: 'none', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(39, 174, 96, 0.35)' },
};

export default SubmissionDetail;
